/// <reference lib="webworker" />

import {
  AddHistoryItemApiAddHistoryItemCommand,
  addHistoryItem,
} from "@opendonationassistant/history-service";
import { reportError, reportStarted } from "../worker-status";
import axios from "axios";
import type { TokenDto } from "../systems";
import { log } from "./log";

const DONATEPAY_SOCKET_TOKEN_URL = "https://donatepay.ru/api/v2/socket/token";
const CENTRIFUGO_WEBSOCKET_URL =
  "wss://centrifugo.donatepay.ru:443/connection/websocket";
const RECONNECT_DELAY_MS = 1000;
const API_TIMEOUT_MS = 15000;

// Endpoint comes from the build-time environment, with a fallback to the
// production gateway.
const HISTORY_API_ENDPOINT =
  process.env.REACT_APP_HISTORY_API_ENDPOINT ?? "https://api.oda.digital";

let connectedTokens: string[] = [];
const activeSockets = new Set<WebSocket>();

// ── DonatePay data shapes ──────────────────────────────────────────

interface DonatePaySettings {
  id: string;
  triggerAlerts: boolean;
  triggerReel: boolean;
  triggerDonaton: boolean;
  countInTop: boolean;
  addToGoal: boolean;
}

interface DonatePayPayment {
  id?: string;
  vars: {
    sum: number;
    currency: string;
    name: string;
    comment: string;
  };
}

/**
 * Centrifugo push message: either the auth reply (id: 1) or a
 * donation event published on the subscribed channel.
 */
interface DonatePayMessage {
  id?: number;
  result?: {
    client?: string;
    channel?: string;
    data?: {
      data?: {
        notification?: DonatePayPayment;
      };
    };
  };
}

// ── DonatePay API helpers ──────────────────────────────────────────

/**
 * Exchange the DonatePay access token for a Centrifugo connection token.
 */
async function getCentrifugoToken(dpToken: string): Promise<string> {
  const response = await axios.post(
    DONATEPAY_SOCKET_TOKEN_URL,
    {
      access_token: dpToken,
    },
    { timeout: API_TIMEOUT_MS },
  );
  return response.data.token;
}

/**
 * Ask the DonatePay backend for a channel subscription token bound to
 * the client id returned by the Centrifugo auth reply.
 */
async function subscribeToChannel(
  dpToken: string,
  channel: string,
  client: string,
): Promise<string> {
  const response = await axios.post(
    DONATEPAY_SOCKET_TOKEN_URL,
    {
      access_token: dpToken,
      channels: [channel],
      client,
    },
    { timeout: API_TIMEOUT_MS },
  );
  return response.data.channels[0].token;
}

// ── WebSocket message handling ─────────────────────────────────────

function handleWebSocketMessage(
  odaToken: string,
  recipientId: string,
  dpToken: string,
  settings: DonatePaySettings,
  channel: string,
  raw: string,
  socket: WebSocket,
): void {
  const message: DonatePayMessage = JSON.parse(raw);

  if (message.id === 1) {
    const client = message.result?.client;
    if (!client) {
      log("ERROR", "DonatePay Centrifugo auth reply missing client id");
      reportError(odaToken, "DonatePay", "Centrifugo auth reply missing client id");
      return;
    }
    subscribeToChannel(dpToken, channel, client)
      .then((channelToken) => {
        log("INFO", `DonatePay subscribed to channel ${channel}`);
        socket.send(
          JSON.stringify({
            params: {
              channel,
              token: channelToken,
            },
            method: 1,
            id: 2,
          }),
        );
      })
      .catch((err) => {
        log("ERROR", "Failed to subscribe to DonatePay channel:", err);
        reportError(odaToken, "DonatePay", `failed to subscribe to channel: ${err}`);
      });
    return;
  }

  if (
    message.result?.channel === channel &&
    message.result?.data?.data?.notification
  ) {
    handleDonation(odaToken, recipientId, settings, message.result.data.data.notification);
  }
}

function handleDonation(
  odaToken: string,
  recipientId: string,
  settings: DonatePaySettings,
  payment: DonatePayPayment,
): void {
  log("INFO",
    `DonatePay donation: ${payment.vars.sum} ${payment.vars.currency} from ${payment.vars.name}`,
  );

  const command: AddHistoryItemApiAddHistoryItemCommand = {
    recipientId,
    amount: {
      minor: 0,
      major: payment.vars.sum,
      currency: payment.vars.currency,
    },
    nickname: payment.vars.name,
    message: payment.vars.comment,
    triggerAlert: settings.triggerAlerts,
    triggerReel: settings.triggerReel,
    triggerDonaton: settings.triggerDonaton,
    goals: [],
    addToTop: settings.countInTop,
    addToGoal: settings.addToGoal,
    paymentId:
      payment.id ??
      hashString(`${payment.vars.name}:${payment.vars.comment}`).toString(),
    system: "DonatePay",
    event: "payment",
    authorizationTimestamp: new Date().toISOString(),
  };

  addHistoryItem({
    baseURL: HISTORY_API_ENDPOINT,
    body: command,
    headers: {
      Authorization: `Bearer ${odaToken}`,
    },
  })
    .then(() =>
      log("INFO",
        `DonatePay donation persisted to history [${payment.vars.sum} ${payment.vars.currency}]`,
      ),
    )
    .catch((err) => {
      log("ERROR", "Failed to persist DonatePay donation to history:", err);
      reportError(odaToken, "DonatePay", `failed to persist donation to history: ${err}`);
    });
}

// ── WebSocket lifecycle ─────────────────────────────────────────────

function startWebSocketClient(
  odaToken: string,
  recipientId: string,
  dpToken: string,
  settings: DonatePaySettings,
  centrifugoToken: string,
): void {
  log("INFO", "Starting DonatePay Centrifugo WebSocket connection");
  const socket = new WebSocket(CENTRIFUGO_WEBSOCKET_URL);
  const channel = `$public:${settings.id}`;
  let reconnecting = false;
  activeSockets.add(socket);

  // Reconnect on close/error (replaces navigate(0) — not possible in a SW).
  // Guarded so error + close firing together only schedules one reconnect.
  const scheduleReconnect = (): void => {
    if (reconnecting) return;
    reconnecting = true;
    setTimeout(() => {
      startConnection(odaToken, recipientId, dpToken, settings);
    }, RECONNECT_DELAY_MS);
  };

  socket.addEventListener("error", (err) => {
    log("ERROR", "DonatePay WebSocket error:", err);
    reportError(odaToken, "DonatePay", `WebSocket error: ${err}`);
    scheduleReconnect();
  });

  socket.addEventListener("open", () => {
    log("INFO", "DonatePay Centrifugo WebSocket opened");
    reportStarted(odaToken, "DonatePay");
    socket.send(
      JSON.stringify({
        params: {
          token: centrifugoToken,
        },
        id: 1,
      }),
    );
  });

  socket.addEventListener("message", (event) => {
    handleWebSocketMessage(
      odaToken,
      recipientId,
      dpToken,
      settings,
      channel,
      event.data as string,
      socket,
    );
  });

  socket.addEventListener("close", (event) => {
    const wasRegistered = activeSockets.delete(socket);
    if (event.code !== 1000) {
      reportError(
        odaToken,
        "DonatePay",
        `WebSocket closed with code ${event.code}${event.reason ? `: ${event.reason}` : ""}`,
      );
    }
    if (!wasRegistered) return; // Closed by deregister — do not reconnect.
    log("INFO",
      "DonatePay Centrifugo WebSocket closed. Reconnection attempt in 1s",
    );
    scheduleReconnect();
  });
}

function startConnection(
  odaToken: string,
  recipientId: string,
  dpToken: string,
  settings: DonatePaySettings,
): void {
  getCentrifugoToken(dpToken)
    .then((centrifugoToken) => {
      startWebSocketClient(odaToken, recipientId, dpToken, settings, centrifugoToken);
    })
    .catch((err) => {
      log("ERROR", "Failed to get DonatePay socket token:", err);
      reportError(odaToken, "DonatePay", `failed to get socket token: ${err}`);
    });
}

// ── Registration (called from logger-worker) ────────────────────────

export function register(
  odaToken: string,
  recipientId: string,
  tokens: TokenDto[] | null,
): void {
  log("INFO", { connected: connectedTokens }, "add donatepay-listener");
  if (!tokens) {
    reportError(odaToken, "DonatePay", "Failed to fetch recipient tokens");
    return;
  }
  tokens
    .filter((t) => t.system === "DonatePay")
    .filter((t) => t.enabled)
    .filter((t) => !connectedTokens.includes(t.id))
    .forEach((t) => {
      log("INFO", `add donatepay handler for ${t.id}`);
      connectedTokens.push(t.id);

      startConnection(
        odaToken,
        recipientId,
        t.token,
        t.settings as unknown as DonatePaySettings,
      );
    });
}

export function deregister(): void {
  log("INFO", { connected: connectedTokens }, "remove donatepay listener");
  activeSockets.forEach((socket) => {
    activeSockets.delete(socket);
    socket.close(1000, "deregistered");
  });
  connectedTokens = [];
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Signed 32-bit string hash (hash * 31 + char, forced to 32-bit).
 * Inlined here to avoid pulling src/utils.ts (and its React/axios
 * imports) into the service worker bundle.
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i); // hash * 31 + char
    hash |= 0; // force 32-bit int
  }
  return hash; // signed 32-bit
}

